import { useEffect } from 'react';
import { useLanguage } from '@/lib/LanguageContext';
import { LANG_CODES, OG_LOCALES, localizePath, stripLang } from '@/lib/langPath';

/**
 * Actualiza el <title>, la meta description, el canonical, las etiquetas
 * hreflang y las Open Graph de la página de forma imperativa, para que cada
 * ruta tenga su propio SEO. Cada idioma vive en su propia dirección, así que se
 * declara el canonical autorreferente de la versión actual y el grupo completo
 * de hreflang (es, ca, en, fr y x-default).
 */
const SITE_ORIGIN = 'https://smartpos.es';

function ensureLink(rel, hreflang) {
  const sel = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let link = document.head.querySelector(sel);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    if (hreflang) link.setAttribute('hreflang', hreflang);
    document.head.appendChild(link);
  }
  return link;
}

function ensureMeta(property) {
  let meta = document.head.querySelector(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  return meta;
}

export default function useDocumentMeta({ title, description, canonicalPath, ogImage } = {}) {
  const { lang } = useLanguage();

  useEffect(() => {
    if (title) document.title = title;

    if (description) {
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'description');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', description);
    }

    if (canonicalPath) {
      const base = stripLang(canonicalPath);
      const url = `${SITE_ORIGIN}${localizePath(base, lang)}`;

      ensureLink('canonical').setAttribute('href', url);
      LANG_CODES.forEach((code) => {
        ensureLink('alternate', code.toLowerCase())
          .setAttribute('href', `${SITE_ORIGIN}${localizePath(base, code)}`);
      });
      ensureLink('alternate', 'x-default')
        .setAttribute('href', `${SITE_ORIGIN}${localizePath(base, 'ES')}`);

      // Actualizar Open Graph dinámicamente para que cada página tenga sus
      // propios metadatos al compartir en redes sociales.
      if (title) ensureMeta('og:title').setAttribute('content', title);
      if (description) ensureMeta('og:description').setAttribute('content', description);
      ensureMeta('og:url').setAttribute('content', url);
      ensureMeta('og:locale').setAttribute('content', OG_LOCALES[lang] || 'es_ES');
    }

    if (ogImage) {
      ensureMeta('og:image').setAttribute('content', ogImage);
    }
  }, [title, description, canonicalPath, ogImage, lang]);
}